import Badge, { BadgeVariant } from '@ben/orion/badge';
import type { RestrictionSet } from '@ben/shared/types/restriction-set';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import styled, { css } from 'styled-components';
import RestrictionGroup from '../restriction-set/restriction-group';
import type { RestrictionType } from '../restriction-set/types';
import { RESTRICTION_CONFIG } from '../restriction-set/use-restriction-field-arrays';

interface DisabledRestrictionSetProps {
  restrictionSet: RestrictionSet;
  availableOptions: { label: string; value: RestrictionType }[];
}

interface RestrictionSetData {
  restrictionSet: RestrictionSet;
}

const DisabledRestrictionSet = ({
  restrictionSet,
  availableOptions,
}: DisabledRestrictionSetProps) => {
  const { control, setValue, reset, trigger } = useForm<RestrictionSetData>({
    defaultValues: {
      restrictionSet,
    },
  });

  useEffect(() => {
    reset({ restrictionSet });
  }, [restrictionSet, reset]);

  const restrictionSetPath = 'restrictionSet';

  return (
    <DisabledFieldset disabled>
      <RestrictionsContainer>
        {availableOptions.map((option) => {
          const type = option.value;
          const items = restrictionSet[type];

          if (!items || items.length === 0) {
            return null;
          }

          const {
            component: FormComponent,
            title,
            needsSetValue,
            needsTrigger,
          } = RESTRICTION_CONFIG[type];

          return (
            <RestrictionGroup key={type} title={title} isOpen={true}>
              <ExistingBadge variant={BadgeVariant.Neutral} text="Existing" />
              {items.map((_, index) => (
                <FormComponent
                  key={index}
                  index={index}
                  control={control}
                  restrictionSetPath={restrictionSetPath}
                  {...(needsSetValue ? { setValue } : {})}
                  {...(needsTrigger ? { trigger } : {})}
                />
              ))}
            </RestrictionGroup>
          );
        })}
      </RestrictionsContainer>
    </DisabledFieldset>
  );
};

export default DisabledRestrictionSet;

const RestrictionsContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    position: relative;
  `};
`;

const DisabledFieldset = styled.fieldset`
  border: none;
  padding: 0;
  margin: 0;
`;

const ExistingBadge = styled(Badge)`
  position: absolute;
  top: 0;
  right: 16px;
`;
